import React from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as BriefCase } from '../../../../assets/images/BriefCase.svg';
import { ReactComponent as IndustriesIcon } from '../../../../assets/images/IndustriesIcon.svg';
import { ReactComponent as SchoolIcon } from '../../../../assets/images/SchoolIcon.svg';
import { ReactComponent as UserBoltIcon } from '../../../../assets/images/tabler-icon-user-bolt.svg';
import Label from '../../../../components/shared/Label/Label';
import {
  EXPERIENCE_LEVEL_KEYS,
  INDUSTRY_KEYS,
  REQUIRED_EDUCATION_KEYS,
  SKILLS_KEYS,
} from '../../../../constants/commonConstants.js';

import styles from './RequiredEducation.module.scss';

const RequiredEducation = ({ skills = [], education, experienceLevel, industries = [] }) => {
  const { t } = useTranslation('common');
  const normalizedSkills = Array.isArray(skills) ? skills : [];
  const normalizedIndustries = Array.isArray(industries) ? industries : [];
  const industryNames = normalizedIndustries
    .map((industry) => industry?.name)
    .filter((industryName) => Boolean(industryName));
  const hasSkills = normalizedSkills.length > 0;
  const normalizedEducation = typeof education === 'string' ? education.trim() : education;
  const normalizedExperience = typeof experienceLevel === 'string' ? experienceLevel.trim() : experienceLevel;
  const hasEducation = normalizedEducation && normalizedEducation !== 'None' && Boolean(normalizedEducation);
  const hasExperience = normalizedExperience && normalizedExperience !== 'None' && Boolean(normalizedExperience);
  const hasIndustries = industryNames.length > 0;

  const getExperienceLevelLabel = (name) =>
    EXPERIENCE_LEVEL_KEYS[name] ? t(`experienceLevel.${EXPERIENCE_LEVEL_KEYS[name]}`) : name;
  const getIndustryLabel = (name) => (INDUSTRY_KEYS[name] ? t(`industry.${INDUSTRY_KEYS[name]}`) : name);
  const getSkillLabel = (name) => (SKILLS_KEYS[name] ? t(`skills.${SKILLS_KEYS[name]}`) : name);
  const getRequiredEducationLabel = (name) =>
    REQUIRED_EDUCATION_KEYS[name] ? t(`requiredEducation.${REQUIRED_EDUCATION_KEYS[name]}`) : name;

  const sections = [
    {
      key: 'experience',
      icon: <UserBoltIcon />,
      title: t('browse.experienceLevel'),
      isVisible: hasExperience,
      content: <span className={styles.TextContainer}>{getExperienceLevelLabel(normalizedExperience)}</span>,
    },
    {
      key: 'industries',
      icon: <IndustriesIcon />,
      title: t('browse.industries'),
      isVisible: hasIndustries,
      content: (
        <div className={styles.moreSkillsContainer}>
          <span className={styles.TextContainer}>{getIndustryLabel(industryNames[0])}</span>
          {industryNames.length > 1 && (
            <Label classname={styles.moreSkillsText} tags={[`+${industryNames.length - 1}`]} />
          )}
        </div>
      ),
    },
    {
      key: 'skills',
      icon: <BriefCase />,
      title: t('filterHeadings.skills'),
      isVisible: hasSkills,
      content: (
        <div className={styles.moreSkillsContainer}>
          <Label tags={normalizedSkills.slice(0, 1).map((skill) => getSkillLabel(skill?.name ?? skill))} />
          {normalizedSkills.length > 1 && (
            <Label classname={styles.moreSkillsText} tags={[`+${normalizedSkills.length - 1}`]} />
          )}
        </div>
      ),
    },
    {
      key: 'education',
      icon: <SchoolIcon />,
      title: t('browse.requiredEducation'),
      isVisible: hasEducation,
      content: <span className={styles.TextContainer}>{getRequiredEducationLabel(normalizedEducation)}</span>,
    },
  ];

  const visibleSections = sections.filter(({ isVisible }) => isVisible);

  if (visibleSections.length === 0) {
    return null;
  }

  return (
    <div className={styles.educationContainer}>
      {visibleSections.map(({ key, icon, title, content }) => (
        <div className={styles.rowData} key={key}>
          {icon}
          <div className={styles.heading}>
            <p className={styles.heading}>{title}</p>
            {content}
          </div>
        </div>
      ))}
    </div>
  );
};

export default RequiredEducation;
